/**
 * Assessments Waitlist 落地页
 * 用于在功能正式上线前收集潜在用户邮箱
 * 基于 AssessmentsLanding 页面修改，聚焦于邮箱收集
 */

import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { useState, useRef, useCallback, useEffect } from "react";
import { useSeoMeta } from "@/hooks/useSeoMeta";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import {
  AnimatedSection,
  StaggerContainer,
  AnimatedCard,
} from "@/components/shared/animations";
import {
  fadeUpVariants,
  scaleFadeVariants,
  stepVariants,
  popInVariants,
  countUpVariants,
  hoverEffects,
  ANIMATION_TIMING,
} from "@/lib/animations";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Brain,
  HelpCircle,
  Video,
  Cpu,
  ClipboardList,
  RefreshCw,
  Star,
  CheckCircle2,
  AlertTriangle,
  Search,
  DollarSign,
  Quote,
  UserPlus,
  Mail,
  Sparkles,
  type LucideIcon,
} from "lucide-react";
import Footer from "@/components/Footer";
import { WaitlistSubscriptionForm } from "@/components/waitlist";

// 图片放大镜组件
interface ImageMagnifierProps {
  src: string;
  alt: string;
  magnifierSize?: number;
  zoomLevel?: number;
}

function ImageMagnifier({
  src,
  alt,
  magnifierSize = 150,
  zoomLevel = 2.5,
}: ImageMagnifierProps) {
  const [showMagnifier, setShowMagnifier] = useState(false);
  const [magnifierPos, setMagnifierPos] = useState({ x: 0, y: 0 });
  const [imgBounds, setImgBounds] = useState({ width: 0, height: 0 });
  const [isLoaded, setIsLoaded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    const img = new Image();
    img.src = src;
    img.onload = () => setIsLoaded(true);
  }, [src]);

  const handleMouseEnter = useCallback(() => {
    if (imgRef.current) {
      const { width, height } = imgRef.current.getBoundingClientRect();
      setImgBounds({ width, height });
    }
    setShowMagnifier(true);
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!imgRef.current) return;
    const { left, top } = imgRef.current.getBoundingClientRect();
    const x = e.clientX - left;
    const y = e.clientY - top;
    setMagnifierPos({ x, y });
  }, []);

  const handleMouseLeave = useCallback(() => {
    setShowMagnifier(false);
  }, []);

  return (
    <div
      className={`relative ${!isMobile ? "cursor-zoom-in" : ""}`}
      onMouseEnter={isMobile ? undefined : handleMouseEnter}
      onMouseMove={isMobile ? undefined : handleMouseMove}
      onMouseLeave={isMobile ? undefined : handleMouseLeave}
    >
      {!isLoaded && (
        <div className="w-full aspect-[16/9] bg-muted/50 rounded-2xl animate-pulse flex items-center justify-center">
          <div className="w-12 h-12 border-4 border-muted-foreground/20 border-t-muted-foreground/60 rounded-full animate-spin" />
        </div>
      )}
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        className={`w-full h-auto object-cover rounded-2xl transition-opacity duration-300 ${isLoaded ? "opacity-100" : "opacity-0 absolute"}`}
        onLoad={() => setIsLoaded(true)}
      />
      {showMagnifier && isLoaded && !isMobile && (
        <div
          className="absolute pointer-events-none border-2 border-green-400/80 rounded-full shadow-xl z-10 overflow-hidden"
          style={{
            width: magnifierSize,
            height: magnifierSize,
            left: magnifierPos.x - magnifierSize / 2,
            top: magnifierPos.y - magnifierSize / 2,
            backgroundImage: `url(${src})`,
            backgroundRepeat: "no-repeat",
            backgroundSize: `${imgBounds.width * zoomLevel}px ${imgBounds.height * zoomLevel}px`,
            backgroundPosition: `-${magnifierPos.x * zoomLevel - magnifierSize / 2}px -${magnifierPos.y * zoomLevel - magnifierSize / 2}px`,
          }}
        >
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "linear-gradient(180deg, transparent 0%, rgba(34, 197, 94, 0.3) 50%, transparent 100%)",
              height: "30%",
              animation: "scanLine 1.5s ease-in-out infinite",
            }}
          />
          <div className="absolute inset-0 rounded-full border-2 border-green-400/50 animate-pulse" />
        </div>
      )}
    </div>
  );
}

// 图标映射
const PAIN_POINT_ICONS: LucideIcon[] = [AlertTriangle, Search, DollarSign];
const FEATURE_ICONS: LucideIcon[] = [Video, Cpu, ClipboardList, RefreshCw];
// Waitlist 专用步骤图标：申请资格、获得邀请、开始分析
const WAITLIST_STEP_ICONS: LucideIcon[] = [UserPlus, Mail, Sparkles];

// 用户证言头像
const TESTIMONIAL_AVATARS = [
  "https://api.dicebear.com/9.x/avataaars/svg?seed=Felix",
  "https://api.dicebear.com/9.x/avataaars/svg?seed=Lily",
  "https://api.dicebear.com/9.x/avataaars/svg?seed=Oliver",
];

export default function AssessmentsWaitlistPage() {
  const { t } = useTranslation("assessments");
  const prefersReducedMotion = useReducedMotion();

  // SEO meta 标签
  useSeoMeta({ namespace: "assessments" });

  // 从翻译文件获取数据
  const painPoints = t("landing.painPoints.items", {
    returnObjects: true,
  }) as Array<{
    title: string;
    description: string;
  }>;

  const features = t("landing.features.items", {
    returnObjects: true,
  }) as Array<{
    title: string;
    description: string;
  }>;

  // Waitlist 专用步骤（从 waitlistPage 命名空间获取）
  const waitlistSteps = t("waitlistPage.steps.items", {
    returnObjects: true,
  }) as Array<{
    title: string;
    description: string;
  }>;

  const stats = t("landing.stats.items", { returnObjects: true }) as Array<{
    value: string;
    label: string;
  }>;

  const testimonials = t("landing.testimonials.items", {
    returnObjects: true,
  }) as Array<{
    name: string;
    tag: string;
    content: string;
  }>;

  const faqs = t("landing.faq.items", { returnObjects: true }) as Array<{
    question: string;
    answer: string;
  }>;

  const trustItems = t("waitlistPage.cta.trustItems", {
    returnObjects: true,
  }) as string[];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section - 修改为 Waitlist 版本 */}
      <section className="pt-8 sm:pt-10 md:pt-16 pb-4 sm:pb-6 md:pb-8">
        <div className="max-w-8xl mx-auto px-4 sm:px-6 text-center">
          <motion.h1
            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold tracking-tight text-foreground mb-4 sm:mb-6 leading-tight"
            initial={prefersReducedMotion ? false : "hidden"}
            animate="visible"
            variants={fadeUpVariants}
            custom={ANIMATION_TIMING.HERO_TITLE_DELAY}
          >
            {t("waitlistPage.hero.title")}
            <span className="block mt-1 sm:mt-2">
              {t("waitlistPage.hero.subtitle")}
            </span>
          </motion.h1>
          <motion.p
            className="text-base sm:text-lg md:text-lg text-muted-foreground mb-6 sm:mb-8 max-w-4xl mx-auto px-2"
            initial={prefersReducedMotion ? false : "hidden"}
            animate="visible"
            variants={fadeUpVariants}
            custom={ANIMATION_TIMING.HERO_DESCRIPTION_DELAY}
          >
            {t("waitlistPage.hero.description")}
          </motion.p>

          {/* Waitlist 订阅表单 - 主 CTA */}
          <motion.div
            className="max-w-md mx-auto mb-4 px-4"
            initial={prefersReducedMotion ? false : "hidden"}
            animate="visible"
            variants={scaleFadeVariants}
            custom={ANIMATION_TIMING.HERO_CTA_DELAY}
          >
            <WaitlistSubscriptionForm
              campaignSlug="assessments-waitlist"
              buttonText={t("waitlistPage.hero.ctaButton")}
              placeholder={t("waitlistPage.hero.emailPlaceholder")}
            />
          </motion.div>

          {/* 社会证明 */}
          <motion.p
            className="text-sm text-muted-foreground mt-6 mb-8 sm:mb-12"
            initial={prefersReducedMotion ? false : "hidden"}
            animate="visible"
            variants={fadeUpVariants}
            custom={ANIMATION_TIMING.HERO_CTA_DELAY + 0.1}
          >
            {t("waitlistPage.hero.socialProof")}
          </motion.p>
        </div>

        {/* 产品主图展示 */}
        <motion.div
          className="max-w-5xl mx-auto px-4 sm:px-6"
          initial={prefersReducedMotion ? false : "hidden"}
          animate="visible"
          variants={fadeUpVariants}
          custom={ANIMATION_TIMING.HERO_SCREENSHOT_DELAY}
        >
          <div className="relative rounded-xl sm:rounded-2xl overflow-hidden shadow-xl sm:shadow-2xl">
            <ImageMagnifier
              src="/images/main-image.jpg"
              alt="AI 观赏鱼状态分析"
              magnifierSize={180}
              zoomLevel={2.5}
            />
          </div>
        </motion.div>
      </section>

      {/* 痛点区域 - 保留 */}
      <section className="py-10 sm:py-12 md:py-16 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <AnimatedSection className="text-center mb-8 sm:mb-12">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-extrabold text-foreground mb-2 sm:mb-3">
              {t("landing.painPoints.sectionTitle")}
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground px-2">
              {t("landing.painPoints.sectionDesc")}
            </p>
          </AnimatedSection>
          <StaggerContainer className="grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-3 md:gap-6">
            {painPoints.map((point, idx) => {
              const Icon = PAIN_POINT_ICONS[idx];
              return (
                <AnimatedCard
                  key={idx}
                  isStaggerChild
                  hoverEffect="lift"
                  className="group relative border border-border bg-card hover:border-foreground/20 hover:shadow-md transition-all duration-300 rounded-lg"
                >
                  <CardHeader className="pb-2 p-3 sm:p-4 md:p-6 md:pb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 sm:w-9 md:w-10 sm:h-9 md:h-10 rounded-lg bg-foreground/5 dark:bg-foreground/10 border border-border flex items-center justify-center">
                        <Icon className="w-4 h-4 sm:w-4 md:w-5 sm:h-4 md:h-5 text-foreground/70" />
                      </div>
                      <Badge
                        variant="outline"
                        className="text-xs border-border text-muted-foreground"
                      >
                        {idx + 1}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-2 p-3 sm:p-4 md:p-6 md:pt-2">
                    <h3 className="text-sm sm:text-base font-semibold text-foreground mb-1.5 sm:mb-2">
                      {point.title}
                    </h3>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                      {point.description}
                    </p>
                  </CardContent>
                </AnimatedCard>
              );
            })}
          </StaggerContainer>
        </div>
      </section>

      {/* AI 功能特点 - 保留 */}
      <section className="py-10 sm:py-12 md:py-16 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <AnimatedSection className="text-center mb-8 sm:mb-12">
            <Badge
              variant="outline"
              className="mb-3 sm:mb-4 px-2.5 sm:px-3 py-1 border-foreground/20 text-xs"
            >
              <Brain className="w-3 h-3 mr-1.5" />
              {t("landing.features.badge")}
            </Badge>
            <h2 className="text-xl sm:text-2xl md:text-3xl font-extrabold text-foreground mb-2 sm:mb-3">
              {t("landing.features.sectionTitle")}
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground max-w-4xl mx-auto px-2">
              {t("landing.features.sectionDesc")}
            </p>
          </AnimatedSection>
          <StaggerContainer className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4 lg:gap-4">
            {features.map((feature, idx) => {
              const Icon = FEATURE_ICONS[idx];
              return (
                <AnimatedCard
                  key={idx}
                  isStaggerChild
                  hoverEffect="none"
                  className="group border border-border bg-background hover:border-foreground/30 hover:shadow-md transition-all duration-300 rounded-lg"
                >
                  <CardContent className="p-3 sm:p-4 md:p-5">
                    <div className="flex items-start gap-2 sm:gap-3 md:gap-4">
                      <motion.div
                        className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-foreground/5 dark:bg-foreground/10 border border-border flex items-center justify-center shrink-0 group-hover:bg-foreground group-hover:border-foreground transition-colors"
                        whileHover={
                          prefersReducedMotion
                            ? undefined
                            : hoverEffects.iconPulse
                        }
                      >
                        <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-foreground/70 group-hover:text-background" />
                      </motion.div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-foreground mb-1 text-xs sm:text-sm">
                          {feature.title}
                        </h3>
                        <p className="text-xs text-muted-foreground leading-relaxed">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </AnimatedCard>
              );
            })}
          </StaggerContainer>
        </div>
      </section>

      {/* 三步流程 - 修改为 Waitlist 版本 */}
      <section className="py-10 sm:py-12 md:py-16 lg:py-28">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <AnimatedSection className="text-center mb-10 sm:mb-16">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-extrabold text-foreground mb-2 sm:mb-3">
              {t("waitlistPage.steps.sectionTitle")}
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground px-2">
              {t("waitlistPage.steps.sectionDesc")}
            </p>
          </AnimatedSection>
          <div className="relative">
            {/* 连接线 - 桌面端 */}
            <motion.div
              className="hidden md:block absolute top-16 left-[calc(16.67%+72px)] right-[calc(16.67%+72px)] h-px z-0 overflow-hidden"
              initial={prefersReducedMotion ? false : { opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3 }}
            >
              <motion.div
                className="w-full h-full border-t border-dashed border-border"
                initial={
                  prefersReducedMotion ? false : { scaleX: 0, originX: 0 }
                }
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2, ease: "easeInOut" }}
              />
            </motion.div>
            {/* 移动端垂直连接线 */}
            <div className="md:hidden absolute left-1/2 top-16 bottom-16 w-px border-l border-dashed border-border -translate-x-1/2 z-0" />
            <StaggerContainer className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-8">
              {waitlistSteps.map((step, idx) => {
                const Icon = WAITLIST_STEP_ICONS[idx];
                return (
                  <motion.div
                    key={idx}
                    className="relative text-center group"
                    variants={stepVariants}
                    custom={idx}
                  >
                    <motion.div
                      className="relative inline-block mb-4 sm:mb-8 z-10"
                      whileHover={
                        prefersReducedMotion ? undefined : hoverEffects.glow
                      }
                    >
                      <div className="absolute inset-0 rounded-xl sm:rounded-2xl bg-gradient-to-br from-foreground/5 to-foreground/10 dark:from-foreground/10 dark:to-foreground/5 blur-xl scale-110 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      <div className="relative w-16 h-16 sm:w-20 md:w-32 sm:h-20 md:h-32 rounded-xl sm:rounded-2xl bg-gradient-to-br from-muted/60 to-muted/30 dark:from-muted/30 dark:to-muted/10 border border-border/80 dark:border-border/50 flex items-center justify-center backdrop-blur-sm group-hover:border-foreground/30 group-hover:shadow-lg transition-all duration-300">
                        <Icon className="w-6 h-6 sm:w-8 md:w-12 sm:h-8 md:h-12 text-foreground/60 group-hover:text-foreground/80 transition-colors duration-300" />
                      </div>
                      <motion.div
                        className="absolute -top-1.5 -right-1.5 sm:-top-2 sm:-right-2 md:-top-3 md:-right-3 w-6 h-6 sm:w-7 md:w-9 sm:h-7 md:h-9 rounded-full bg-foreground text-background flex items-center justify-center text-[10px] sm:text-xs md:text-sm font-bold shadow-lg ring-2 sm:ring-2 md:ring-4 ring-background"
                        variants={popInVariants}
                      >
                        {idx + 1}
                      </motion.div>
                    </motion.div>
                    <h3 className="text-base sm:text-lg font-semibold text-foreground mb-2 sm:mb-3">
                      {step.title}
                    </h3>
                    <p className="text-xs sm:text-sm text-muted-foreground max-w-[200px] sm:max-w-[220px] mx-auto leading-relaxed">
                      {step.description}
                    </p>
                  </motion.div>
                );
              })}
            </StaggerContainer>
          </div>
        </div>
      </section>

      {/* 数据统计 & 用户证言 - 保留 */}
      <section className="py-10 sm:py-12 md:py-16 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <AnimatedSection className="text-center mb-8 sm:mb-12">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-extrabold text-foreground mb-2 sm:mb-3">
              {t("landing.stats.sectionTitle")}
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground px-2">
              {t("landing.stats.sectionDesc")}
            </p>
          </AnimatedSection>

          <AnimatedSection delay={0.1}>
            <Card className="border border-border bg-background mb-8 sm:mb-12">
              <CardContent className="p-0">
                <div className="grid grid-cols-3 divide-x divide-border">
                  {stats.map((stat, idx) => (
                    <motion.div
                      key={idx}
                      className="text-center py-4 sm:py-5 md:py-8 px-2 sm:px-4"
                      initial={prefersReducedMotion ? false : "hidden"}
                      whileInView="visible"
                      viewport={{ once: true }}
                      variants={countUpVariants}
                      transition={{ delay: idx * 0.1 }}
                    >
                      <div className="text-lg sm:text-xl md:text-3xl lg:text-4xl font-bold text-foreground mb-0.5 sm:mb-1">
                        {stat.value}
                      </div>
                      <div className="text-[9px] sm:text-[10px] md:text-xs lg:text-sm text-muted-foreground leading-tight">
                        {stat.label}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </AnimatedSection>

          <div>
            <AnimatedSection>
              <h3 className="text-base sm:text-lg font-semibold text-foreground text-center mb-6 sm:mb-8">
                {t("landing.testimonials.title")}
              </h3>
            </AnimatedSection>
            <StaggerContainer className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 sm:gap-4">
              {testimonials.map((testimonial, idx) => (
                <AnimatedCard
                  key={idx}
                  isStaggerChild
                  hoverEffect="glow"
                  className="border border-border bg-background hover:border-foreground/20 transition-colors rounded-lg"
                >
                  <CardContent className="p-4 sm:p-5">
                    <Quote className="w-5 h-5 sm:w-6 sm:h-6 text-foreground/20 mb-2 sm:mb-3" />
                    <p className="text-xs sm:text-sm text-foreground mb-3 sm:mb-4 leading-relaxed">
                      {testimonial.content}
                    </p>
                    <Separator className="mb-3 sm:mb-4" />
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 sm:gap-3">
                        <Avatar className="w-7 h-7 sm:w-8 sm:h-8 border border-border">
                          <AvatarImage
                            src={TESTIMONIAL_AVATARS[idx]}
                            alt={testimonial.name}
                          />
                          <AvatarFallback className="bg-foreground/5 dark:bg-foreground/10 text-foreground text-xs">
                            {testimonial.name.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="text-xs sm:text-sm font-medium text-foreground">
                            {testimonial.name}
                          </p>
                          <p className="text-[10px] sm:text-xs text-muted-foreground">
                            {testimonial.tag}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-0.5">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <Star
                            key={i}
                            className="w-2.5 h-2.5 sm:w-3 sm:h-3 fill-foreground/70 text-foreground/70"
                          />
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </AnimatedCard>
              ))}
            </StaggerContainer>
          </div>
        </div>
      </section>

      {/* FAQ - 保留 */}
      <section className="py-10 sm:py-12 md:py-16 lg:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <AnimatedSection className="text-center mb-8 sm:mb-10">
            <Badge
              variant="outline"
              className="mb-3 sm:mb-4 px-2.5 sm:px-3 py-1 border-foreground/20 text-xs"
            >
              <HelpCircle className="w-3 h-3 mr-1.5" />
              {t("landing.faq.badge")}
            </Badge>
            <h2 className="text-xl sm:text-2xl md:text-3xl font-extrabold text-foreground mb-2 sm:mb-3">
              {t("landing.faq.sectionTitle")}
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground px-2">
              {t("landing.faq.sectionDesc")}
            </p>
          </AnimatedSection>
          <AnimatedSection delay={0.1}>
            <Accordion type="single" collapsible className="w-full space-y-2">
              {faqs.map((faq, idx) => (
                <AccordionItem
                  key={idx}
                  value={`item-${idx}`}
                  className="border border-border rounded-lg px-3 sm:px-4 data-[state=open]:bg-muted/20 dark:data-[state=open]:bg-muted/10 [&:last-child]:border-b"
                >
                  <AccordionTrigger className="text-left text-xs sm:text-sm font-medium hover:no-underline py-3 sm:py-4 min-h-[44px]">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-xs sm:text-sm text-muted-foreground leading-relaxed pb-3 sm:pb-4">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </AnimatedSection>
        </div>
      </section>

      {/* CTA Section - 修改为 Waitlist 版本 */}
      <section className="py-10 sm:py-12 md:py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <AnimatedSection>
            <Card className="border border-foreground/10 bg-foreground text-background overflow-hidden rounded-xl sm:rounded-2xl">
              <CardContent className="p-5 sm:p-6 md:p-8 lg:p-12 text-center relative">
                <div className="relative z-10">
                  <h2 className="text-xl sm:text-2xl md:text-3xl font-extrabold mb-3 sm:mb-4">
                    {t("waitlistPage.cta.title")}
                    <br />
                    {t("waitlistPage.cta.subtitle")}
                  </h2>
                  <p className="text-background/60 mb-6 sm:mb-8 max-w-2xl mx-auto text-xs sm:text-sm md:text-sm px-2">
                    {t("waitlistPage.cta.description")}
                  </p>

                  {/* Waitlist 表单 - 替代原来的按钮 */}
                  <div className="max-w-md mx-auto mb-5 sm:mb-6">
                    <WaitlistSubscriptionForm
                      campaignSlug="assessments-waitlist"
                      buttonText={t("waitlistPage.cta.ctaButton")}
                      placeholder={t("waitlistPage.cta.emailPlaceholder")}
                      className="[&_input]:bg-background/10 [&_input]:border-background/20 [&_input]:text-background [&_input]:placeholder:text-background/50 [&_button]:bg-background [&_button]:text-foreground [&_button]:hover:bg-background/90"
                    />
                  </div>

                  <Separator className="bg-background/10 mb-5 sm:mb-6" />

                  <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:justify-center sm:gap-3 md:gap-4 lg:gap-6 text-[10px] sm:text-xs text-background/60">
                    {trustItems.map((item, idx) => (
                      <span
                        key={idx}
                        className="flex items-center gap-1 sm:gap-1.5"
                      >
                        <CheckCircle2 className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </AnimatedSection>
        </div>
      </section>

      {/* 免责声明 - 保留 */}
      <section className="py-4 sm:py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <p className="text-[10px] sm:text-xs text-muted-foreground/60 text-center leading-relaxed">
            {t("landing.disclaimer")}
          </p>
          <p className="text-[10px] sm:text-xs text-muted-foreground/40 text-center leading-relaxed mt-1.5 sm:mt-2">
            {t("landing.stats.disclaimer")}
          </p>
        </div>
      </section>

      <div className="relative z-50">
        <Footer />
      </div>
    </div>
  );
}
